import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, getApiErrorMessage } from '../../../api/client';
import { unwrap } from '../../../lib/api-envelope';
import CancelledLoadNotice from '../../../components/trucking/CancelledLoadNotice';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import type { BolSignResponse, LoadUpdateResponse } from '../../../types/load';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Download,
} from 'lucide-react';

/**
 * The `/trucking/loads/bols/:id` payload, as the handler actually selects it
 * (trucking-loads.routes.ts). Typed here rather than inferred from a mock:
 * the previous fallback object WAS the de-facto type, so every field the API
 * does not send looked present and every field it does send that the mock
 * lacked looked absent.
 */
interface BolCargoItem {
  id?: string;
  description?: string;
  qty?: number;
  weight?: string;
  condition?: string;
}

interface BolDetail {
  id?: string;
  bol_number?: string;
  status?: string;
  /** Set only on a cancelled load (fe#815 cancel). */
  cancel_reason?: string | null;
  cancelled_at?: string | null;
  shipper_name?: string;
  driver_name?: string;
  truck_unit?: string;
  license_plate?: string;
  customer_name?: string;
  site_reference?: string;
  pickup_location?: string;
  delivery_location?: string;
  cargo?: BolCargoItem[];
  total_weight?: string | number;
  departed_at?: string;
  arrived_at?: string;
  drive_time?: string;
  wait_time_minutes?: number | null;
  standby_time_minutes?: number | null;
  /** api#950: a per_km load cannot be signed without its total km. */
  billing_type?: string | null;
  km_driven?: string | number | null;
  driver_signature?: string | null;
  receiver_signature?: string | null;
  receiver_name?: string | null;
  receiver_title?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Signature Canvas                                                   */
/* ------------------------------------------------------------------ */
function SignatureCanvas({
  label,
  onSign,
}: {
  label: string;
  onSign: (data: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  const getPos = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    if (!drawing.current || !lastPos.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPos.current = null;
    const data = canvasRef.current?.toDataURL('image/png') || '';
    onSign(data);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onSign('');
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
          {label}
        </span>
        <button onClick={clear} className="text-[10px] text-fieldiq-accent font-semibold">
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        className="w-full h-28 bg-fieldiq-dark border border-fieldiq-border rounded-lg touch-none"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Screen                                                        */
/* ------------------------------------------------------------------ */

/**
 * A signed load's read-only view draws the stored signature, or says there is
 * none. It used to draw a hard-coded green squiggle when a signature was
 * missing and badge both boxes "Signed" regardless (#803), so a load with no
 * signature on file looked signed by both parties.
 */
function SignedBadge({ signed }: { signed: boolean }) {
  return signed ? (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">Signed</span>
  ) : (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-fieldiq-border text-fieldiq-muted">Not signed</span>
  );
}

export default function DriverBOLScreen() {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { bolId } = useParams<{ bolId: string }>();
  const timeZone = useTenantTimeZone();

  /* ---- Fetch BOL ---- */
  const { data: bol, isLoading, isError, error } = useQuery({
    queryKey: ['driver-bol', bolId],
    queryFn: () =>
      apiClient
        .get(`/trucking/loads/bols/${bolId}`)
        .then((r) => unwrap<BolDetail>(r.data)),
    enabled: !!bolId,
  });

  /* ---- Signature state ---- */
  const [driverSig, setDriverSig] = useState<string | null>(null);
  const [receiverSig, setReceiverSig] = useState<string | null>(null);
  const [receiverName, setReceiverName] = useState('');
  const [receiverTitle, setReceiverTitle] = useState('');

  /* ---- Wait / standby time ---- */
  const [waitTime, setWaitTime] = useState('');
  const [standbyTime, setStandbyTime] = useState('');

  /* ---- Total km, for a per-km load (api#950) ---- */
  // The customer's signature settles km, and a per-km load with none is 409
  // LOAD_KM_MISSING. The odometer difference the delivery records is one way,
  // so the driver enters the TOTAL here, return trip included, and it is saved
  // to the load before the signature is sent.
  const [kmText, setKmText] = useState('');
  const perKm = bol?.billing_type === 'per_km';

  /* ---- Cargo conditions ---- */
  const [cargoConditions, setCargoConditions] = useState<Record<number, string>>({});

  // Seed the form from the BOL once it arrives. This used to be the initial
  // value of six useState calls, which read from a fabricated BOL — Apex
  // Hauling, ConocoPhillips Canada, a drawworks assembly — whenever the fetch
  // had not resolved or had failed.
  useEffect(() => {
    if (!bol) return;
    setDriverSig(bol.driver_signature ?? null);
    setReceiverSig(bol.receiver_signature ?? null);
    setReceiverName(bol.receiver_name || '');
    setReceiverTitle(bol.receiver_title || '');
    setWaitTime(bol.wait_time_minutes != null ? String(bol.wait_time_minutes) : '');
    setStandbyTime(bol.standby_time_minutes != null ? String(bol.standby_time_minutes) : '');
    setKmText(bol.km_driven != null && bol.km_driven !== '' ? String(bol.km_driven) : '');
    const init: Record<number, string> = {};
    (bol.cargo ?? []).forEach((_, i) => {
      init[i] = 'no_damage';
    });
    setCargoConditions(init);
  }, [bol]);

  /* ---- Submit ---- */
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isSigned =
    bol?.status === 'signed' ||
    bol?.status === 'delivered' ||
    bol?.status === 'completed';

  const kmValue = kmText.trim() === '' ? null : Number(kmText.trim());
  const kmOk = !perKm || (kmValue !== null && Number.isFinite(kmValue) && kmValue >= 0);
  const signaturesReady = !!driverSig && !!receiverSig && receiverName.trim().length > 0;
  const canSubmit = signaturesReady && kmOk;
  /** Minutes typed, or null for a blank box. A blank is "not entered", never 0. */
  const minutes = (t: string) => {
    const n = parseInt(t, 10);
    return Number.isFinite(n) ? n : null;
  };

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // The total km first, so the signature finds them on the load. Only
      // when it changed: an unchanged value is already there.
      // Compared as numbers: NUMERIC arrives as a string ('486.00').
      const savedKm = bol?.km_driven == null || bol.km_driven === '' ? null : Number(bol.km_driven);
      if (perKm && kmValue !== null && kmValue !== savedKm) {
        await apiClient.patch<LoadUpdateResponse>(`/trucking/loads/${bolId}`, { kmDriven: kmValue });
      }
      await apiClient.post<BolSignResponse>(`/trucking/loads/bols/${bolId}/sign`, {
        driver_signature: driverSig,
        receiver_signature: receiverSig,
        receiver_name: receiverName,
        receiver_title: receiverTitle,
        // null for a blank box: the API keeps the wait the driver already
        // logged (COALESCE). `|| 0` sent 0 and overwrote it at the signature.
        wait_time_minutes: minutes(waitTime),
        standby_time_minutes: minutes(standbyTime),
        cargo_conditions: (bol?.cargo ?? []).map((item, i) => ({
          line_item_id: item.id ?? i,
          condition: cargoConditions[i] || 'no_damage',
        })),
      });
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['driver-bol', bolId] });
      queryClient.invalidateQueries({ queryKey: ['driver-recent-bols'] });
    } catch (err) {
      // The screen used to advance to "BOL submitted — PDF sent to
      // dispatcher" on the failure path, which is the worst place in this app
      // to lie: the receiver has already signed, the driver drives away, and
      // the load is still unsigned on the server. The signatures stay on
      // screen so the submit can be retried without collecting them again.
      setSubmitError(getApiErrorMessage(err, 'Failed to submit BOL'));
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    'w-full h-12 px-3 text-sm text-white bg-fieldiq-dark border border-fieldiq-border rounded-lg placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent';

  /* ---- Loading state ---- */
  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-fieldiq-muted animate-spin" />
      </div>
    );
  }

  /* ---- Refusal / not found ---- */
  // A BOL that did not load is not a BOL to sign. The ownership guards on the
  // loads router answer 404 for another company's id, and that used to render
  // as a complete, signable document belonging to nobody.
  if (isError || !bol) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <div
          className="border-b border-fieldiq-border px-4 py-3"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
        >
          <button onClick={() => navigate(-1)} className="text-fieldiq-muted">
            <ArrowLeft size={20} />
          </button>
        </div>
        <div className="max-w-md mx-auto px-4 py-10 text-center space-y-3">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" />
          <p className="text-sm font-semibold text-white">
            This bill of lading could not be loaded.
          </p>
          <p className="text-xs text-fieldiq-muted">
            {isError
              ? getApiErrorMessage(error, 'The server did not return this BOL.')
              : 'The server returned no BOL for this link.'}
          </p>
        </div>
      </div>
    );
  }

  // A cancelled load is final (fe#815 cancel): read-only, no wait time, no
  // signatures, no edit. It stays in the driver's BOL list marked Cancelled.
  if (bol.status === 'cancelled') {
    return (
      <CancelledLoadNotice
        bolNumber={bol.bol_number}
        reason={bol.cancel_reason}
        cancelledAt={bol.cancelled_at}
        timeZone={timeZone}
        onBack={() => navigate(-1)}
      />
    );
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      {/* ---- Header (sticky) ---- */}
      <div
        className="sticky top-0 z-30 bg-fieldiq-dark border-b border-fieldiq-border px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-fieldiq-muted">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-base font-bold text-white">BOL #{bol.bol_number}</h1>
              <p className="text-xs text-fieldiq-muted">{bol.customer_name}</p>
            </div>
          </div>
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              isSigned || submitted
                ? 'bg-green-500/15 text-green-400'
                : 'bg-amber-500/15 text-amber-400'
            }`}
          >
            {isSigned || submitted ? 'Signed' : 'Pending Sig'}
          </span>
        </div>
      </div>

      {/* ---- Content ---- */}
      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Section 1 -- Parties */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
            Parties
          </h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Shipper</span>
              <span className="text-white font-medium">{bol.shipper_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Driver</span>
              <span className="text-white">{bol.driver_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Truck</span>
              <span className="text-white">{bol.truck_unit}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Licence</span>
              <span className="text-white">{bol.license_plate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Customer</span>
              <span className="text-white font-medium">{bol.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Site Ref</span>
              <span className="text-white">{bol.site_reference}</span>
            </div>
          </div>
        </div>

        {/* Section 2 -- Route */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-3">
            Route
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] text-fieldiq-muted uppercase">Pickup</p>
              <p className="text-sm font-bold text-white font-mono">{bol.pickup_location}</p>
            </div>
            <span className="text-fieldiq-muted">&rarr;</span>
            <div className="flex-1 text-right">
              <p className="text-[10px] text-fieldiq-muted uppercase">Delivery</p>
              <p className="text-sm font-bold text-white font-mono">{bol.delivery_location}</p>
            </div>
          </div>
        </div>

        {/* Section 3 -- Cargo */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-3">
            Cargo
          </h3>
          <div className="space-y-2">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-2 text-[10px] text-fieldiq-muted uppercase font-bold">
              <span className="col-span-5">Item</span>
              <span className="col-span-1 text-center">Qty</span>
              <span className="col-span-3 text-right">Weight (kg)</span>
              <span className="col-span-3 text-right">Condition</span>
            </div>
            {(bol.cargo || []).map((item: any, i: number) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 text-sm border-t border-fieldiq-border pt-2"
              >
                <span className="col-span-5 text-white">{item.description}</span>
                <span className="col-span-1 text-center text-white">{item.qty}</span>
                <span className="col-span-3 text-right text-white font-mono">{item.weight}</span>
                <span className="col-span-3 text-right text-fieldiq-muted text-xs">
                  {item.condition || 'No damage'}
                </span>
              </div>
            ))}
            {/* Total */}
            <div className="grid grid-cols-12 gap-2 text-sm border-t border-fieldiq-border pt-2 font-bold">
              <span className="col-span-6 text-fieldiq-muted">Total</span>
              <span className="col-span-3 text-right text-white font-mono">
                {bol.total_weight} kg
              </span>
              <span className="col-span-3" />
            </div>
          </div>
        </div>

        {/* Section 4 -- Load Details */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
            Load Details
          </h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Departed pickup</span>
              <span className="text-white">{bol.departed_at}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Arrived destination</span>
              <span className="text-white">{bol.arrived_at}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fieldiq-muted">Drive time</span>
              <span className="text-white font-medium">{bol.drive_time}</span>
            </div>
          </div>

          {/* Total km for a per-km load, before the customer signs (api#950). */}
          {perKm && !isSigned && !submitted && (
            <div className="mt-3">
              <label htmlFor="bol-total-km" className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1">
                Total km, including the return trip
              </label>
              <input
                id="bol-total-km"
                type="number"
                inputMode="decimal"
                value={kmText}
                onChange={(e) => setKmText(e.target.value)}
                className={inputCls}
              />
            </div>
          )}

          {/* Editable wait / standby times (only when pending) */}
          {!isSigned && !submitted && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1">
                  Wait time (min)
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={waitTime}
                  onChange={(e) => setWaitTime(e.target.value)}
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1">
                  Standby time (min)
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={standbyTime}
                  onChange={(e) => setStandbyTime(e.target.value)}
                  placeholder="0"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          {/* Read-only wait/standby for signed */}
          {(isSigned || submitted) && (
            <div className="space-y-1.5 text-sm mt-2">
              <div className="flex justify-between">
                <span className="text-fieldiq-muted">Wait time at pickup</span>
                <span className="text-white">{bol.wait_time_minutes != null ? `${bol.wait_time_minutes} min` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-fieldiq-muted">Standby time at site</span>
                <span className="text-white">{bol.standby_time_minutes != null ? `${bol.standby_time_minutes} min` : '—'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Section 5 -- Signatures */}
        {isSigned ? (
          /* ---- Signed (read-only) ---- */
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-4">
            <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
              Signatures
            </h3>

            {/* Driver signature */}
            <div role="group" aria-label="Driver signature">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-fieldiq-muted">Driver</span>
                <SignedBadge signed={!!bol.driver_signature} />
              </div>
              <div className="h-20 bg-fieldiq-dark border border-fieldiq-border rounded-lg flex items-center justify-center">
                {bol.driver_signature ? (
                  <img src={bol.driver_signature} alt="Driver signature" className="max-h-16" />
                ) : (
                  <p className="text-xs text-fieldiq-muted">Not signed</p>
                )}
              </div>
              <p className="text-xs text-white mt-1">{bol.driver_name}</p>
            </div>

            {/* Receiver signature */}
            <div role="group" aria-label="Receiver signature">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-fieldiq-muted">Receiver</span>
                <SignedBadge signed={!!bol.receiver_signature} />
              </div>
              <div className="h-20 bg-fieldiq-dark border border-fieldiq-border rounded-lg flex items-center justify-center">
                {bol.receiver_signature ? (
                  <img src={bol.receiver_signature} alt="Receiver signature" className="max-h-16" />
                ) : (
                  <p className="text-xs text-fieldiq-muted">Not signed</p>
                )}
              </div>
              <p className="text-xs text-white mt-1">
                {bol.receiver_name || 'Receiver'}
                {bol.receiver_title ? ` \u2014 ${bol.receiver_title}` : ''}
              </p>
            </div>

            {/* Download PDF button only */}
            <button className="w-full h-12 rounded-xl border border-fieldiq-border text-white text-sm font-semibold flex items-center justify-center gap-2 min-h-[48px]">
              <Download size={16} />
              Download PDF
            </button>
          </div>
        ) : submitted ? (
          /* ---- Submitted success ---- */
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 text-center space-y-3">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto" />
            <p className="text-sm font-bold text-green-400">
              BOL submitted &mdash; PDF sent to dispatcher
            </p>
            <button className="inline-flex items-center gap-2 text-xs font-semibold text-fieldiq-accent min-h-[48px]">
              <Download size={14} />
              Download my copy
            </button>
          </div>
        ) : (
          /* ---- Pending -- signature pads ---- */
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-4">
            <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
              Signatures
            </h3>

            {/* Driver signature */}
            <div>
              <p className="text-[10px] text-fieldiq-muted mb-1">
                I confirm the above cargo was loaded as described
              </p>
              <SignatureCanvas
                label="Driver Signature"
                onSign={(data) => setDriverSig(data || null)}
              />
            </div>

            {/* Receiver signature */}
            <div>
              <p className="text-[10px] text-fieldiq-muted mb-1">
                Received in the condition noted above
              </p>
              <SignatureCanvas
                label="Receiver Signature"
                onSign={(data) => setReceiverSig(data || null)}
              />
            </div>

            {/* Receiver info */}
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Receiver name"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                className={inputCls}
              />
              <input
                type="text"
                placeholder="Receiver title (optional)"
                value={receiverTitle}
                onChange={(e) => setReceiverTitle(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* Submit */}
            {submitError && (
              <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="text-xs font-semibold text-red-300">
                    Not submitted — this BOL is still unsigned.
                  </p>
                  <p className="text-xs text-red-200/80">{submitError}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className={`w-full h-14 rounded-xl text-sm font-bold min-h-[48px] ${
                canSubmit
                  ? 'bg-fieldiq-accent text-fieldiq-dark'
                  : 'bg-fieldiq-card text-fieldiq-muted border border-fieldiq-border opacity-50'
              }`}
            >
              {submitting
                ? 'Submitting...'
                : canSubmit
                  ? 'Submit BOL'
                  : signaturesReady
                    ? 'Enter the total km first'
                    : 'Both signatures & receiver name required'}
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
